import {useEffect, useMemo} from "react";
import {ActivityIndicator, View} from "react-native";
import {router} from "expo-router";
import {useAppData} from "@/data/app-data-hooks";
import {useLastSelectedOrg, useSetLastSelectedOrg} from "@/data/user-data-hooks";
import NoOrgAvailable from "@/components/organization/NoOrgAvailable";
import {OrganizationSelectionScreen} from "@/components/organization/OrganizationSelectionScreen";
import {useCurrentTheme} from "@/theming/useCurrentTheme";

export default function Index() {
    const {data} = useAppData();
    const {data: lastSelectedOrgId, isLoading: isLoadingLastOrg} = useLastSelectedOrg();
    const setLastSelectedOrg = useSetLastSelectedOrg();
    const {theme} = useCurrentTheme();

    const orgIds = useMemo(() => Object.keys(data), [data]);
    const orgCount = orgIds.length;

    const organizations = useMemo(() =>
        orgIds.map(id => ({id, organization: data[id].organization})),
        [data, orgIds]
    );

    useEffect(() => {
        if (isLoadingLastOrg) return;

        if (orgCount === 1) {
            // Single org: auto-redirect and persist
            const orgId = orgIds[0];
            setLastSelectedOrg.mutate(orgId);
            router.replace(`/organization/${orgId}`);
        } else if (orgCount > 1 && lastSelectedOrgId && orgIds.includes(lastSelectedOrgId)) {
            // Multiple orgs with valid remembered selection: auto-redirect
            router.replace(`/organization/${lastSelectedOrgId}`);
        }
        // Other cases (0 orgs, multiple orgs without valid selection) are handled in render
    }, [orgCount, orgIds, lastSelectedOrgId, isLoadingLastOrg, setLastSelectedOrg]);

    // Loading state while checking last selected org
    if (isLoadingLastOrg) {
        return (
            <View style={{flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: theme.colors.background}}>
                <ActivityIndicator size="large" color={theme.colors.primary}/>
            </View>
        );
    }

    // 0 orgs
    if (orgCount === 0) {
        return <NoOrgAvailable/>;
    }

    // Multiple orgs without valid remembered selection: show selection screen
    if (orgCount > 1 && (!lastSelectedOrgId || !orgIds.includes(lastSelectedOrgId))) {
        return <OrganizationSelectionScreen organizations={organizations}/>;
    }

    // Redirecting state (single org or valid remembered selection)
    return (
        <View style={{flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: theme.colors.background}}>
            <ActivityIndicator size="large" color={theme.colors.primary}/>
        </View>
    );
}
